import { validate } from 'email-validator';

export const MAX_LENGTH_DEFAULT_STRING = 255;

export enum ValidationResult {
  TOO_LONG = 'TOO_LONG',
  WRONG_FORMAT = 'WRONG_FORMAT',
  EMPTY = 'EMPTY',
  OUT_OF_RANGE = 'OUT_OF_RANGE',
  OUT_OF_RANGE_MIN = 'OUT_OF_RANGE_MIN',
  OUT_OF_RANGE_MAX = 'OUT_OF_RANGE_MAX',
  VALID = 'VALID',
  NOT_EQUAL = 'NOT_EQUAL',
  DUPLICATE = 'DUPLICATE',
}

export enum ValidationMode {
  ON_BLUR = 'ON_BLUR',
  ALWAYS = 'ALWAYS',
  OFF = 'OFF',
}

export type ValidatorFn<T = string> = (input: T) => ValidationResult;

type OptionalString = string | null | undefined;

export function normalizeString(string: string): string;
export function normalizeString(string: OptionalString): string | undefined;
export function normalizeString(string: OptionalString): string | undefined {
  return string?.trim();
}

/**
 * Checks if the input string is of expected format and reasonable length
 * @param email Input string which should look like an email e.g. test@mail.com
 * @returns Enum status as the Validation Result
 */
export const validateEmail = (email: OptionalString) => {
  if (email === undefined || email === null) {
    return ValidationResult.EMPTY;
  }
  if (typeof email !== 'string') {
    return ValidationResult.WRONG_FORMAT;
  }
  email = normalizeString(email);
  if (!email) {
    return ValidationResult.EMPTY;
  }
  if (email.length > MAX_LENGTH_DEFAULT_STRING) {
    return ValidationResult.TOO_LONG;
  }
  if (!validate(email)) {
    return ValidationResult.WRONG_FORMAT;
  }
  return ValidationResult.VALID;
};

/**
 * Checks if the input string is of desired format and length
 * @param string The input string
 * @param maxLength Max number of characters in the string
 * @param regex The Regular Expression that the string should match
 * @returns Enum status as the Validation Result
 */
export const validateString = (
  string: OptionalString,
  maxLength = MAX_LENGTH_DEFAULT_STRING,
  regex?: string,
): ValidationResult => {
  if (string === undefined || string === null) {
    return ValidationResult.EMPTY;
  }
  if (typeof string !== 'string') {
    return ValidationResult.WRONG_FORMAT;
  }
  string = normalizeString(string);
  if (!string) {
    return ValidationResult.EMPTY;
  }
  if (string.length > maxLength) {
    return ValidationResult.TOO_LONG;
  }
  if (regex && !new RegExp(regex).exec(string)) {
    return ValidationResult.WRONG_FORMAT;
  }
  return ValidationResult.VALID;
};

export const validateRegex = (
  regexString: OptionalString,
): ValidatorFn<OptionalString> => {
  if (!regexString) {
    return () => ValidationResult.VALID;
  }

  const regex = new RegExp(regexString);
  return (input: OptionalString) => {
    if (input === undefined || input === null) {
      return ValidationResult.EMPTY;
    }
    if (typeof input !== 'string') {
      return ValidationResult.WRONG_FORMAT;
    }
    return regex.exec(input.trim())
      ? ValidationResult.VALID
      : ValidationResult.WRONG_FORMAT;
  };
};
